<template>
    <section class="offer-preset-restrictions">
        <h3 class="offer-preset-restrictions__title">{{ label('header') }}</h3>

        <div class="offer-preset-restrictions__row">
            <v-checkbox
                :model-value="!isNil(offerMechanic.maximumPerCustomer)"
                :label="label('maxTriggerPerCustomer')"
                density="compact"
                hide-details
                @update:model-value="checked => update({ maximumPerCustomer: checked ? 1 : null })"
            />
            <v-text-field
                :model-value="offerMechanic.maximumPerCustomer"
                :disabled="isNil(offerMechanic.maximumPerCustomer)"
                :aria-label="label('maxTriggerPerCustomer')"
                type="number"
                min="1"
                density="compact"
                variant="outlined"
                hide-details
                class="offer-preset-restrictions__number"
                @update:model-value="
                    value => update({ maximumPerCustomer: toPositiveInteger(value) })
                "
            />
        </div>

        <div class="offer-preset-restrictions__row">
            <v-checkbox
                :model-value="!isNil(offerMechanic.maximumPerPurchase)"
                :label="label('maxTriggerPerPurchase')"
                density="compact"
                hide-details
                @update:model-value="checked => update({ maximumPerPurchase: checked ? 1 : null })"
            />
            <v-text-field
                :model-value="offerMechanic.maximumPerPurchase"
                :disabled="isNil(offerMechanic.maximumPerPurchase)"
                :aria-label="label('maxTriggerPerPurchase')"
                type="number"
                min="1"
                density="compact"
                variant="outlined"
                hide-details
                class="offer-preset-restrictions__number"
                @update:model-value="
                    value => update({ maximumPerPurchase: toPositiveInteger(value) })
                "
            />
        </div>

        <div v-if="showSingleTrip" class="offer-preset-restrictions__field">
            <span class="offer-preset-restrictions__label">{{ label('storeVisitCondition') }}</span>
            <v-radio-group
                :model-value="offerMechanic.isSingleTripPromo"
                inline
                density="compact"
                hide-details
                @update:model-value="value => update({ isSingleTripPromo: value })"
            >
                <v-radio :label="label('single')" :value="true" />
                <v-radio :label="label('multiple')" :value="false" />
            </v-radio-group>
        </div>

        <div class="offer-preset-restrictions__field">
            <span class="offer-preset-restrictions__label">{{ label('to') }}</span>
            <v-select
                :model-value="offerMechanic.customerAvailability"
                :items="customerAvailabilityOptions"
                :aria-label="label('to')"
                density="compact"
                variant="outlined"
                hide-details
                @update:model-value="value => update({ customerAvailability: value })"
            />
        </div>

        <div class="offer-preset-restrictions__field">
            <span class="offer-preset-restrictions__label">{{ label('how') }}</span>
            <v-select
                :model-value="offerMechanic.promotionAvailability"
                :items="promotionAvailabilityOptions"
                :aria-label="label('how')"
                density="compact"
                variant="outlined"
                hide-details
                @update:model-value="value => update({ promotionAvailability: value })"
            />
        </div>

        <div class="offer-preset-restrictions__row">
            <v-checkbox
                v-model="isCouponEnabled"
                :label="label('withCoupon').replace(/:$/, '')"
                density="compact"
                hide-details
            />
            <v-text-field
                :model-value="offerMechanic.couponBarcode"
                :disabled="!isCouponEnabled"
                :aria-label="label('withCoupon')"
                maxlength="14"
                inputmode="numeric"
                density="compact"
                variant="outlined"
                hide-details
                class="offer-preset-restrictions__coupon"
                @update:model-value="value => update({ couponBarcode: toCouponBarcode(value) })"
            />
        </div>
    </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { isNil } from 'lodash';
import featureFlags from '@enums/feature-flags';
import promotionAvailability from '@enums/promotion-availability';
import i18n from '@/js/vue-i18n';
import { toSentenceCase } from '@/js/utils/string-utils';

const I18N_BASE = 'planning.promotionsMaintenance.offer.offerMechanic.restrictions';

const props = defineProps({
    offerMechanic: { type: Object, required: true },
});
const emit = defineEmits(['update']);
const store = useStore();

const label = key => toSentenceCase(i18n.global.t(`${I18N_BASE}.${key}`));
const showSingleTrip = computed(
    () => !!store.state.clientConfig.toggleLogic?.[featureFlags.showSingleTrip]
);
const customerAvailabilityOptions = computed(() =>
    (store.getters['clientConfig/getCustomerRestrictions'] || []).map(option => ({
        title: toSentenceCase(option.description),
        value: option.key,
    }))
);
const promotionAvailabilityOptions = computed(() => [
    { title: label('promotionAvailabilities.onPurchase'), value: promotionAvailability.onPurchase },
    {
        title: label('promotionAvailabilities.handInCoupon'),
        value: promotionAvailability.handInCoupon,
    },
]);

function update(patch) {
    emit('update', patch);
}

const isCouponEnabled = ref(!isNil(props.offerMechanic.couponBarcode));
watch(isCouponEnabled, enabled => {
    if (!enabled) update({ couponBarcode: null });
});

const toPositiveInteger = value => {
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? null : Math.max(1, parsed);
};
const toCouponBarcode = value => {
    const digits = String(value ?? '')
        .replace(/\D/g, '')
        .slice(0, 14);
    return digits || null;
};
</script>

<style scoped lang="scss">
.offer-preset-restrictions {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    padding: 1.6rem 2rem;
    font-size: 1.4rem;
    border: 1px solid #dfe3e8;
    border-radius: 0.8rem;

    &__title {
        margin: 0;
        font-size: 1.6rem;
        font-weight: 600;
    }

    &__row {
        display: flex;
        gap: 1.2rem;
        align-items: center;
        justify-content: space-between;
    }

    &__field {
        display: flex;
        flex-direction: column;
        gap: 0.4rem;
    }

    &__label {
        font-size: 1.3rem;
        font-weight: 600;
    }

    &__number {
        flex: 0 0 9rem;
    }

    &__coupon {
        flex: 0 0 16rem;
    }

    :deep(.v-label),
    :deep(.v-field__input),
    :deep(.v-select__selection-text) {
        font-size: 1.4rem;
    }
}
</style>
